import React from "react";
import { useAuth } from "../../common/auth";
import { decryptAccount } from "../../common/payoutKey/crypto";
import { LockedPayoutAccount, logPayoutUnlock } from "../../common/requests/transactions/endpoints";
import { usePayoutKey } from "./PayoutKeyProvider";

/** sellerId -> account number / PayNow id, once the key is unlocked. Each
 * newly shown seller is reported to the activity log once per unlock. */
export function useUnlockedAccounts(accounts: Record<string, LockedPayoutAccount | null | undefined>, where: string) {
  const { key } = usePayoutKey();
  const { idToken } = useAuth();
  const [plain, setPlain] = React.useState<Record<string, string>>({});
  const logged = React.useRef<Set<string>>(new Set());
  const entries = Object.entries(accounts).filter((e): e is [string, LockedPayoutAccount] => !!e[1]);
  const signature = entries.map(([id, a]) => `${id}:${a.ciphertext.slice(0, 16)}`).join("|");

  React.useEffect(() => {
    if (!key) {
      setPlain({});
      logged.current = new Set();
      return;
    }
    let alive = true;
    (async () => {
      const next: Record<string, string> = {};
      for (const [id, account] of entries) {
        try {
          next[id] = await decryptAccount(key, account.ciphertext);
        } catch {
          /* saved under another key: stays locked */
        }
      }
      if (!alive) return;
      setPlain(next);
      const fresh = Object.keys(next).filter((id) => !logged.current.has(id));
      if (fresh.length && idToken) {
        fresh.forEach((id) => logged.current.add(id));
        logPayoutUnlock(fresh, where, idToken).catch(() => {});
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, signature, where, idToken]);

  return plain;
}
